import {
  ACCESSORIES_PROPERTIES,
  BIOMETRIC_DEVICE_TYPES,
  DEVICE_UNITS,
  USER_MEASUREMENTS,
  WELLNESS_HUB_DEVICE_TYPE,
} from '@/constants/device';
import { get, isEmpty, isNil, pick } from 'lodash';
import * as Yup from 'yup';
import { CATEGORIES } from '../utils';

export const CATEGORY_PRODUCTCLASS_MAPPING = {
  [CATEGORIES.SMART_HOME]: ['smart_home', 'environmental_sensor'],
  [CATEGORIES.MOTION]: ['environmental_sensor', WELLNESS_HUB_DEVICE_TYPE],
  [CATEGORIES.BIOMETRIC]: ['wellness'],
};

export const CATEGORY_CONDITIONTYPE_MAPPING = {
  [CATEGORIES.SMART_HOME]: [
    'indoor_temperature',
    'contact_sensor',
    'occupancy',
    'toilet_flush',
    'indoor_humidity',
    'ambient_light',
    'smart_plug',
  ],
  [CATEGORIES.MOTION]: ['motion'],
  [CATEGORIES.BIOMETRIC]: [
    'blood_pressure_systolic',
    'blood_pressure_diastolic',
    'glucose_meter',
    'weight_scale',
    'pulse',
    'spo2',
    'thermometer',
  ],
};

export const MOTION_CONDITION_OPERATORS = {
  no_motion_detected: 'less_than',
  some_motion_detected: 'greater_than',
};

export const getSupportedConditionTypesForCategory = (categories) => {
  if (isEmpty(categories)) return [];
  return Object.values(pick(CATEGORY_CONDITIONTYPE_MAPPING, categories)).flat();
};

const isTemperatureCondition = (conditionType, units = []) =>
  conditionType === BIOMETRIC_DEVICE_TYPES.THERMOMETER ||
  String(conditionType || '').includes('temperature') ||
  units.includes(DEVICE_UNITS.TEMPERATURE.F) ||
  units.includes(DEVICE_UNITS.TEMPERATURE.C);

const normalizeText = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

export const findTemplateByThreshold = (templates, value) => {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) {
    return null;
  }

  return (
    templates.find((template) => normalizeText(template?.threshold) === normalizedValue) || null
  );
};

export const findTemplateByName = (templates, templateName) => {
  const normalizedTemplateName = normalizeText(templateName);

  if (!normalizedTemplateName) {
    return null;
  }

  return (
    templates.find((template) => normalizeText(template?.name) === normalizedTemplateName) || null
  );
};

export const resolveMotionConditionOperator = ({ value, templateName, fallbackOperator }) => {
  const normalizedValue = normalizeText(value);
  const normalizedTemplateName = normalizeText(templateName);

  if (normalizedValue === 'no_motion_detected' || normalizedTemplateName === 'no_motion_detected') {
    return MOTION_CONDITION_OPERATORS.no_motion_detected;
  }

  if (
    normalizedValue === 'some_motion_detected' ||
    normalizedTemplateName === 'some_motion_detected'
  ) {
    return MOTION_CONDITION_OPERATORS.some_motion_detected;
  }

  return fallbackOperator;
};

// Maps raw schema unit tokens to their display form.
const formatUnit = (unit) => {
  const displayMap = { C: '°C', F: '°F' };
  return displayMap[unit] || unit;
};

export const getDisplayedConditionUnitLabel = (
  conditionType,
  measurementSystem = USER_MEASUREMENTS.IMPERIAL,
  conditionTypes = {}
) => {
  const units = conditionTypes[conditionType]?.units || [];
  const isTempCondition = isTemperatureCondition(conditionType, units);

  if (!units.length) {
    if (isTempCondition) {
      return measurementSystem === USER_MEASUREMENTS.METRIC
        ? formatUnit(DEVICE_UNITS.TEMPERATURE.C)
        : formatUnit(DEVICE_UNITS.TEMPERATURE.F);
    }
    return '';
  }

  if (isTempCondition) {
    return measurementSystem === USER_MEASUREMENTS.METRIC
      ? formatUnit(DEVICE_UNITS.TEMPERATURE.C)
      : formatUnit(DEVICE_UNITS.TEMPERATURE.F);
  }

  if (units.length === 1) {
    return formatUnit(units[0]);
  }

  // Multi-unit types (e.g. temperature: ['C','F'], weight: ['lbs','kg']):
  // pick the metric or imperial variant based on user preference.
  const metricUnitSet = new Set(['C', 'kg']);
  const selectedUnit =
    measurementSystem === USER_MEASUREMENTS.METRIC
      ? units.find((unit) => metricUnitSet.has(unit)) || units[0]
      : units.find((unit) => !metricUnitSet.has(unit)) || units[0];

  return formatUnit(selectedUnit);
};

export const getConditionPayloadUnit = (
  conditionType,
  measurementSystem = USER_MEASUREMENTS.IMPERIAL,
  conditionTypes = {}
) => {
  const units = conditionTypes[conditionType]?.units || [];
  const isTempCondition = isTemperatureCondition(conditionType, units);
  const hasTemperatureUnits =
    units.includes(DEVICE_UNITS.TEMPERATURE.F) && units.includes(DEVICE_UNITS.TEMPERATURE.C);
  const hasWeightUnits =
    units.includes(DEVICE_UNITS.WEIGHT.LBS) && units.includes(DEVICE_UNITS.WEIGHT.KG);

  if (hasTemperatureUnits || isTempCondition) {
    return measurementSystem === USER_MEASUREMENTS.METRIC
      ? DEVICE_UNITS.TEMPERATURE.C
      : DEVICE_UNITS.TEMPERATURE.F;
  }

  if (hasWeightUnits) {
    return measurementSystem === USER_MEASUREMENTS.METRIC
      ? DEVICE_UNITS.WEIGHT.KG
      : DEVICE_UNITS.WEIGHT.LBS;
  }

  return '';
};

export const createCondition = (type, conditionTypesMap = {}, conditionTypeOrderArray = []) => {
  const defaultType = type || conditionTypeOrderArray[0] || Object.keys(conditionTypesMap)[0];
  const typeConfig =
    conditionTypesMap[defaultType] || conditionTypesMap[conditionTypeOrderArray[0]];

  if (!typeConfig) {
    return {
      type: defaultType || '',
      device: '',
      operator: '',
      value: '',
    };
  }

  const selectedOperator =
    typeConfig.operators.find((operator) => operator.value === typeConfig.defaultOperator) ||
    typeConfig.operators[0];

  return {
    type: defaultType,
    device: typeConfig.devices[0]?.value || '',
    operator: selectedOperator?.value || '',
    value:
      typeConfig.valueType === 'number'
        ? typeConfig.defaultValue
        : typeConfig.defaultValue || typeConfig.values?.[0]?.value || '',
  };
};

export const weekDays = [
  { key: 'sunday', label: 'S' },
  { key: 'monday', label: 'M' },
  { key: 'tuesday', label: 'T' },
  { key: 'wednesday', label: 'W' },
  { key: 'thursday', label: 'R' },
  { key: 'friday', label: 'F' },
  { key: 'saturday', label: 'S' },
];

export const createInitialValues = (conditionTypesMap = {}, conditionTypeOrderArray = []) => ({
  ruleName: '',
  category: '',
  enabled: true,
  notify: false,
  conditions:
    conditionTypeOrderArray.length > 0
      ? [createCondition(undefined, conditionTypesMap, conditionTypeOrderArray)]
      : [],
  days: {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
  },
  startTime: '00:00',
  startTimezone: 'UTC',
  endTime: '00:00',
  endTimezone: 'UTC',
});

// For backwards compatibility
export const initialValues = {
  ruleName: '',
  category: '',
  enabled: true,
  notify: false,
  conditions: [],
  days: {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
  },
  startTime: '00:00',
  startTimezone: 'UTC',
  endTime: '00:00',
  endTimezone: 'UTC',
};

export const createRuleInitialValues = (
  overrides = {},
  conditionTypesMap = {},
  conditionTypeOrderArray = []
) => {
  const defaultInitialValues = createInitialValues(conditionTypesMap, conditionTypeOrderArray);
  return {
    ...defaultInitialValues,
    ...overrides,
    conditions: (overrides.conditions || defaultInitialValues.conditions).map((condition) => ({
      ...condition,
    })),
    days: {
      ...defaultInitialValues.days,
      ...(overrides.days || {}),
    },
  };
};

const getConditionValueForDisplay = (condition, measurementSystem = USER_MEASUREMENTS.IMPERIAL) => {
  const rawValue = condition?.value;

  if (Array.isArray(rawValue)) {
    const conditionType = condition?.id;
    if (isTemperatureCondition(conditionType)) {
      const desiredTemperatureUnit =
        measurementSystem === USER_MEASUREMENTS.METRIC
          ? DEVICE_UNITS.TEMPERATURE.C
          : DEVICE_UNITS.TEMPERATURE.F;

      const matchingTemperatureEntry = rawValue.find(
        (entry) => String(entry?.unit || '').toLowerCase() === desiredTemperatureUnit.toLowerCase()
      );

      return matchingTemperatureEntry?.value ?? rawValue[0]?.value ?? '';
    }

    const desiredUnitByType = {
      weight_scale:
        measurementSystem === USER_MEASUREMENTS.METRIC
          ? DEVICE_UNITS.WEIGHT.KG
          : DEVICE_UNITS.WEIGHT.LBS,
    };

    const desiredUnit = desiredUnitByType[conditionType];

    if (!desiredUnit) {
      return rawValue[0]?.value ?? '';
    }

    const matchingEntry = rawValue.find(
      (entry) => String(entry?.unit || '').toLowerCase() === desiredUnit.toLowerCase()
    );

    return matchingEntry?.value ?? rawValue[0]?.value ?? '';
  }

  return rawValue ?? '';
};

export const formatUIJsonToComponentState = (
  uiJson,
  accessories,
  measurementSystem = USER_MEASUREMENTS.IMPERIAL
) => {
  const accessoryByMacAddress = (accessories || []).reduce((lookup, accessory) => {
    const candidateMacAddresses = [
      get(accessory, ACCESSORIES_PROPERTIES.DEVICE_MAC_ADDRESS),
      get(accessory, ACCESSORIES_PROPERTIES.BASE_MAC_ADDRESS),
      ...(Array.isArray(accessory?.mac) ? accessory.mac.map((entry) => entry?.value) : []),
    ]
      .filter(Boolean)
      .map((macAddress) => String(macAddress).trim().toLowerCase());

    candidateMacAddresses.forEach((macAddress) => {
      lookup[macAddress] = accessory;
    });

    return lookup;
  }, {});

  const getLastModifiedValue = (rule) => String(rule?.last_modified || '').trim();

  const sortedRules = [...(uiJson || [])].sort((leftRule, rightRule) => {
    const leftLastModified = getLastModifiedValue(leftRule);
    const rightLastModified = getLastModifiedValue(rightRule);
    const leftHasLastModified = Boolean(leftLastModified);
    const rightHasLastModified = Boolean(rightLastModified);

    if (leftHasLastModified !== rightHasLastModified) {
      return leftHasLastModified ? -1 : 1;
    }

    if (!leftHasLastModified) {
      return 0;
    }

    const leftTimestamp = Date.parse(leftLastModified);
    const rightTimestamp = Date.parse(rightLastModified);
    const leftIsValidTimestamp = !Number.isNaN(leftTimestamp);
    const rightIsValidTimestamp = !Number.isNaN(rightTimestamp);

    if (leftIsValidTimestamp && rightIsValidTimestamp && leftTimestamp !== rightTimestamp) {
      return rightTimestamp - leftTimestamp;
    }

    if (leftIsValidTimestamp !== rightIsValidTimestamp) {
      return leftIsValidTimestamp ? -1 : 1;
    }

    const lastModifiedAlphabeticalSort = leftLastModified.localeCompare(rightLastModified);

    if (lastModifiedAlphabeticalSort !== 0) {
      return lastModifiedAlphabeticalSort;
    }

    return String(leftRule?.rulename || '').localeCompare(String(rightRule?.rulename || ''));
  });

  return sortedRules.map((rule, index) => ({
    id: rule.ruleuuid || `rule-${index}`,
    expanded: false,
    values: {
      ruleName: rule.rulename || '',
      category: rule.category === 'user' ? 'smart_home' : rule.category || '',
      enabled: Boolean(rule.enabled),
      notify: Boolean(rule.notify),
      startTimezone: rule.timezone || '',
      endTimezone: rule.timezone || '',
      days: weekDays.reduce((acc, day) => {
        acc[day.key] = (rule.days_of_week || []).includes(day.key);
        return acc;
      }, {}),
      startTime: rule.start_time || '',
      endTime: rule.end_time || '',
      conditions: (rule.conditions || []).map((condition) => ({
        type: condition.id,
        device: (condition.devices || []).map((device) => {
          const normalizedMacAddress = String(device?.macaddress || '')
            .trim()
            .toLowerCase();
          const matchedAccessory = accessoryByMacAddress[normalizedMacAddress];

          return (
            get(matchedAccessory, ACCESSORIES_PROPERTIES.DEVICE_UUID) ||
            get(device, ACCESSORIES_PROPERTIES.DEVICE_UUID) ||
            ''
          );
        }),
        operator: condition.operator || '',
        value: getConditionValueForDisplay(condition, measurementSystem),
        condition_template_name: condition.condition_template_name || '',
      })),
    },
  }));
};

const HH_MM_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const validationSchema = (
  conditionTypes = {},
  availableConditionTypeOptions = [],
  getDeviceOptionsForConditionType,
  trans
) => {
  return Yup.object({
    ruleName: Yup.string().trim().required(trans('RULEBUILDER_VALIDATION_RULE_NAME_REQUIRED')),
    category: Yup.string().required(trans('RULEBUILDER_VALIDATION_CATEGORY_REQUIRED')),
    enabled: Yup.boolean().default(initialValues.enabled),
    notify: Yup.boolean()
      .default(initialValues.notify)
      .test(
        'notify-requires-enabled',
        trans('RULEBUILDER_VALIDATION_NOTIFY_REQUIRES_ENABLED'),
        function validateNotify(notifyValue) {
          return notifyValue ? this.parent?.enabled === true : true;
        }
      ),
    days: Yup.object({
      monday: Yup.boolean().default(initialValues.days.monday),
      tuesday: Yup.boolean().default(initialValues.days.tuesday),
      wednesday: Yup.boolean().default(initialValues.days.wednesday),
      thursday: Yup.boolean().default(initialValues.days.thursday),
      friday: Yup.boolean().default(initialValues.days.friday),
      saturday: Yup.boolean().default(initialValues.days.saturday),
      sunday: Yup.boolean().default(initialValues.days.sunday),
    }).test('at-least-one-day-selected', trans('RULEBUILDER_VALIDATION_DAYS_REQUIRED'), (days) =>
      Object.values(days).some(Boolean)
    ),
    startTime: Yup.string()
      .default(initialValues.startTime)
      .required(trans('RULEBUILDER_VALIDATION_START_TIME_REQUIRED'))
      .matches(HH_MM_TIME_PATTERN, trans('RULEBUILDER_VALIDATION_TIME_INVALID')),
    endTime: Yup.string()
      .default(initialValues.endTime)
      .required(trans('RULEBUILDER_VALIDATION_END_TIME_REQUIRED'))
      .matches(HH_MM_TIME_PATTERN, trans('RULEBUILDER_VALIDATION_TIME_INVALID')),
    startTimezone: Yup.string().nullable().default(initialValues.startTimezone),
    endTimezone: Yup.string().nullable().default(initialValues.endTimezone),
    conditions: Yup.array()
      .of(
        Yup.object({
          type: Yup.string()
            .required(trans('RULEBUILDER_VALIDATION_CONDITION_TYPE_REQUIRED'))
            .test(
              'one-of-allowed-condition-types',
              trans('RULEBUILDER_VALIDATION_CONDITION_TYPE_INVALID'),
              function validateConditionType(conditionType) {
                return (availableConditionTypeOptions || [])
                  .map((option) => option.value)
                  .includes(conditionType);
              }
            ),
          device: Yup.array()
            .of(Yup.string().trim())
            .test(
              'one-of-allowed-devices',
              trans('RULEBUILDER_VALIDATION_DEVICE_INVALID'),
              function validateDevices(deviceValues) {
                if (isEmpty(deviceValues)) return true;
                const conditionType = this.parent?.type;
                const allowedDevices = getDeviceOptionsForConditionType(conditionType);
                const allowedDeviceIds = (allowedDevices || []).map((device) => device.value);
                return deviceValues.every((deviceId) => allowedDeviceIds.includes(deviceId));
              }
            ),
          operator: Yup.string()
            .required(trans('RULEBUILDER_VALIDATION_OPERATOR_REQUIRED'))
            .test(
              'one-of-allowed-operators',
              trans('RULEBUILDER_VALIDATION_OPERATOR_INVALID'),
              function validateOperator(operatorValue) {
                const conditionType = this.parent?.type;
                const conditionConfig = conditionTypes[conditionType] || {};
                if (isEmpty(conditionConfig)) return false;
                const allowedOperators = conditionConfig.operators.map((op) => op.value);
                return allowedOperators.includes(operatorValue);
              }
            ),
          value: Yup.mixed()
            .required(trans('RULEBUILDER_VALIDATION_VALUE_REQUIRED'))
            .test(
              'value-valid-for-condition',
              trans('RULEBUILDER_VALIDATION_VALUE_INVALID'),
              function validateConditionValue(rawValue) {
                const conditionType = this.parent?.type;
                const conditionConfig = conditionTypes[conditionType] || {};
                if (isEmpty(conditionConfig)) return false;

                if (conditionConfig.valueType === 'number') {
                  if (rawValue === '' || isNil(rawValue)) return false;
                  const numericValue = Number(rawValue);
                  if (!Number.isFinite(numericValue)) return false;
                  const numericThreshold = conditionConfig.valueRanges;
                  if (
                    numericThreshold &&
                    (numericValue < numericThreshold.min || numericValue > numericThreshold.max)
                  ) {
                    const outOfRangeMessage = trans('RULEBUILDER_VALIDATION_VALUE_OUT_OF_RANGE')
                      .replace('{min}', String(numericThreshold.min))
                      .replace('{max}', String(numericThreshold.max));
                    return this.createError({ message: outOfRangeMessage });
                  }
                  return true;
                }

                const allowedValues = (conditionConfig.values || []).map(
                  (valueOption) => valueOption.value
                );
                return allowedValues.includes(String(rawValue ?? ''));
              }
            ),
        })
      )
      .min(1, trans('RULEBUILDER_VALIDATION_CONDITIONS_REQUIRED')),
  });
};
